import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import Select from 'react-select'
import Modal from 'components/_UI/Modal'
import Button from 'components/_UI/Button'
import Loader from 'components/_UI/Loader'
import Text from 'components/_UI/Text'
import Radio from 'components/_UI/Radio'
import { useEvent } from 'context/events/Event'
import labelsApi from 'api/requests/labels'
import ticketsApi from 'api/requests/tickets'
import downloadBlob from 'helpers/downloadBlob'
import colors from 'theme/colors'
import fonts from 'theme/fonts'
import { Column } from 'styled/alignment/Column'
import { Printer, Receipt, Ticket as TicketIcon, Download } from 'lucide-react'
import { panelAccent } from 'theme/panelAccent'

const PRINT_FORMATS = [
    { value: 'a4_10', icon: Printer, title: 'Impressora comum', subtitle: 'Papel de etiquetas A4, 10 por folha' },
    { value: 'a4_16', icon: Printer, title: 'Impressora comum', subtitle: 'Papel de etiquetas A4, 16 por folha' },
    { value: 'a4_18', icon: Printer, title: 'Impressora comum', subtitle: 'Papel de etiquetas A4, 18 por folha' },
    { value: 'a4_21', icon: Printer, title: 'Impressora comum', subtitle: 'Papel de etiquetas A4, 21 por folha' },
    { value: 'thermal_dk1201', icon: Receipt, title: 'Impressora térmica', subtitle: 'Papel de etiquetas Brother DK1201' },
]

const ALL_TICKET_TYPES = { label: 'Todos os tipos de ingresso', value: null }

function LabelsGenerateModal({ visible, setVisible, toast }) {
    const { event } = useEvent()

    const [printFormat, setPrintFormat] = useState('a4_16')
    const [ticketType, setTicketType] = useState(ALL_TICKET_TYPES)
    const [ticketOptions, setTicketOptions] = useState([ALL_TICKET_TYPES])
    const [generating, setGenerating] = useState(false)

    useEffect(() => {
        fetchTickets()
    }, [])

    async function fetchTickets() {
        try {
            const { data } = await ticketsApi.getTicketForEvent(event?.slug)
            const options = (data?.tickets || []).map(t => ({ label: t.name || t.title, value: t.id }))
            setTicketOptions([ALL_TICKET_TYPES, ...options])
        } catch (error) {}
    }

    async function handleGenerate() {
        setGenerating(true)

        try {
            const payload = {
                print_format: printFormat,
                ticket_type_ids: ticketType?.value ? [ticketType.value] : [],
            }

            const { data } = await labelsApi.generate(event?.slug, payload)
            downloadBlob(data, `etiquetas-${event?.slug}.pdf`, 'pdf')
            setVisible(false)
        } catch (error) {
            toast?.error('Erro ao gerar etiquetas. Tente novamente.', { position: 'top-center' })
        } finally {
            setGenerating(false)
        }
    }

    return (
        <Modal
            isOpen={visible}
            onClose={() => setVisible(false)}
            size='medium'
            childrenOverflowY='scroll'
        >
            <FormContainer>
                <SectionHeader>
                    <SectionIcon><Printer size={18} color={panelAccent(colors.occasionalPurple)} /></SectionIcon>
                    <HeaderTexts>
                        <Text type='bold' color={colors.nightDark} paddingBottom={0}>
                            Definir método e formato de impressão
                        </Text>
                        <Text name='small' color={colors.night} paddingBottom={0}>
                            Escolha o papel usado desta vez e quem recebe etiqueta.
                        </Text>
                    </HeaderTexts>
                </SectionHeader>

                <Text name='small' type='bold' color={colors.nightDark} paddingBottom={0}>
                    Selecione o formato de impressão
                </Text>
                <Column marginTop='8px'>
                    {PRINT_FORMATS.map(format => (
                        <Radio
                            key={format.value}
                            selected={printFormat === format.value}
                            icon={format.icon}
                            label={
                                <PrintFormatLabel>
                                    <strong>{format.title}</strong>
                                    <span>{format.subtitle}</span>
                                </PrintFormatLabel>
                            }
                            textSize={fonts.sizes.small}
                            onSelect={() => setPrintFormat(format.value)}
                        />
                    ))}
                </Column>

                <FieldRow>
                    <FieldLabel><TicketIcon size={14} />Selecione os participantes</FieldLabel>
                    <Select
                        value={ticketType}
                        options={ticketOptions}
                        onChange={setTicketType}
                    />
                    <FieldHint>Deixe em "Todos os tipos de ingresso" para incluir todo mundo.</FieldHint>
                </FieldRow>

                {generating ? (
                    <Column marginTop='24px'>
                        <Loader />
                    </Column>
                ) : (
                    <ButtonsRow>
                        <CancelButton type="button" onClick={() => setVisible(false)}>
                            Cancelar
                        </CancelButton>
                        <Button variant='featured' onClick={handleGenerate}>
                            <Download color={colors.neutralLight} size={16} />
                            <Text color={colors.neutralLight} paddingBottom={0} marginLeft={'6px'}>
                                Gerar etiquetas
                            </Text>
                        </Button>
                    </ButtonsRow>
                )}
            </FormContainer>
        </Modal>
    )
}

export default LabelsGenerateModal

const FormContainer = styled(Column)`
    width: 100%;
    text-align: left;
`

const SectionHeader = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 10px;
    margin-bottom: 20px;
`

const HeaderTexts = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
`

const SectionIcon = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: 8px;
    background: #F3EAFF;
    flex-shrink: 0;
`

const PrintFormatLabel = styled.span`
    display: flex;
    flex-direction: column;
    line-height: 1.4;

    strong {
        font-size: ${fonts.sizes.small};
        color: ${colors.nightDark};
    }

    span {
        font-size: ${fonts.sizes.mini};
        color: ${colors.nightLight};
    }
`

const FieldRow = styled.div`
    margin-top: 24px;
`

const FieldLabel = styled.label`
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: ${fonts.sizes.small};
    font-weight: 600;
    color: ${colors.nightDark};
    margin-bottom: 6px;
`

const FieldHint = styled.span`
    display: block;
    font-size: ${fonts.sizes.mini};
    color: ${colors.nightLight};
    margin-top: 6px;
`

const ButtonsRow = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    margin-top: 28px;
`

const CancelButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 10px 24px;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    border: 1px solid ${colors.neutralDark};
    background: ${colors.neutralBackground};
    color: ${colors.night};

    &:hover {
        background: ${colors.neutralDark};
        color: ${colors.nightDark};
    }
`
